import { extractUtms, getCookies, getEmptyUtmData, hasUtmData, syncUtmDataWithCurrentSession, withoutDefaultUtmData } from "../../app/helpers/commonFunctions";
import { DEFAULT_VALUE, UTM_DATA } from "../types/LoggingTypes";
import type { SessionCampaignPayload } from "../types/LoggingTypes";
import { getEmptyCampaignPayload } from "../helpers/campaignPayload";

const CAMPAIGN_PAYLOAD_STORAGE_KEY = 'sessionCampaignPayload';

const normalizeUtmSource = (source: string): string => {
    const normalizedSource = source.trim().toLowerCase();
    const invalidSources = ['share', 'na', '{utm}', String.fromCharCode(36) + '{utm}'];

    if (!normalizedSource) {
        return '';
    }

    return !invalidSources.includes(normalizedSource)
        ? normalizedSource
        : 'opensooq';
}

export class SessionDimensions {
    private static instance : SessionDimensions;
    
    private _sessionId : string = DEFAULT_VALUE;
    private _sessionSearchId: string = DEFAULT_VALUE;
    private _sessionMedium: string = "";
    private _sessionSource: string = "";
    private _sessionCampaign: string = "";
    private _sessionCampaignPayload?: SessionCampaignPayload;

    constructor(){
        // The sync inside setUtmDataInStorage empties a rotated session's stored payload, so
        // the restore has to read storage after it rather than before.
        this.setUtmDataInStorage();
        this.restoreCampaignPayloadFromStorage();
    }
    
    public static getInstance(){
        if(!SessionDimensions.instance)
        {
            SessionDimensions.instance = new SessionDimensions();
        }
        return SessionDimensions.instance;
    }

    public static clearUtmDimensions(){
        SessionDimensions.clearCampaignPayload();

        if(!SessionDimensions.instance)
        {
            return;
        }
        SessionDimensions.instance._sessionSource = '';
        SessionDimensions.instance._sessionCampaign = '';
        SessionDimensions.instance._sessionMedium = '';
    }

    // Storage is the source of truth here because the session sync can reach this from the
    // constructor, before the singleton is assigned.
    private static clearCampaignPayload(){
        if(!sessionStorage.getItem(CAMPAIGN_PAYLOAD_STORAGE_KEY))
        {
            return;
        }

        const emptyCampaignPayload = getEmptyCampaignPayload();
        sessionStorage.setItem(
            CAMPAIGN_PAYLOAD_STORAGE_KEY,
            JSON.stringify(emptyCampaignPayload)
        );

        if(SessionDimensions.instance)
        {
            SessionDimensions.instance._sessionCampaignPayload = emptyCampaignPayload;
        }
    }
    
    get sessionId():string {
        return this._sessionId;
    }
    
    set sessionId(_sessionId:string)
    {
        this._sessionId = _sessionId;
    }
    
    get sessionSearchId():string {
        return this._sessionSearchId;
    }
    
    set sessionSearchId(_sessionSearchId:string)
    {
        this._sessionSearchId = _sessionSearchId;
    }
    
    get sessionLife():string {
        if(getCookies().tgtTimestamp)
        {
            const sessionLifeTime = Math.floor( ( (Date.now()/1000) - getCookies().tgtTimestamp ) );
            if(sessionLifeTime < 0)
            return "1";
        return sessionLifeTime.toString();
        }
        return DEFAULT_VALUE;
    }
    
    get sessionMedium():string {
        return this._sessionMedium    
    }
    
    set sessionMedium(sessionMedium: string)
    {
        this._sessionMedium = sessionMedium;
    }
    
    get sessionSource():string {
        return this._sessionSource 
    }
    
    set sessionSource(sessionSource: string)
    {
        this._sessionSource = normalizeUtmSource(sessionSource);
    }
    
    get sessionCampaign():string {
        return this._sessionCampaign    
    }
    
    set sessionCampaign(sessionCampaign: string)
    {
        this._sessionCampaign = sessionCampaign;
    }

    get sessionCampaignPayload(): SessionCampaignPayload | undefined {
        return this._sessionCampaignPayload;
    }
    set sessionCampaignPayload(sessionCampaignPayload: SessionCampaignPayload | undefined)
    {
        this._sessionCampaignPayload = sessionCampaignPayload;

        if (sessionCampaignPayload) {
            sessionStorage.setItem(
                CAMPAIGN_PAYLOAD_STORAGE_KEY,
                JSON.stringify(sessionCampaignPayload)
            );
        }
    }

    public setUtmDataInStorage(utmData?: UTM_DATA){
        // The sync clears the utm dimensions on its own when the session has rotated.
        const sessionChanged = syncUtmDataWithCurrentSession();
        let resolvedUtmData = getEmptyUtmData();

        if (utmData) {
            resolvedUtmData = withoutDefaultUtmData(utmData);
        } else if (!sessionChanged) {
            resolvedUtmData = withoutDefaultUtmData(extractUtms());
        }

        // A session without utms keeps nothing in storage, so the entry is removed rather
        // than left behind holding empty values.
        if (!hasUtmData(resolvedUtmData)) {
            sessionStorage.removeItem('utmData');
            return;
        }

        const utmDataObj : UTM_DATA = {
            cCampaign: resolvedUtmData.cCampaign,
            cSource: resolvedUtmData.cSource,
            cMedium: resolvedUtmData.cMedium
        }
        sessionStorage.setItem('utmData',JSON.stringify(utmDataObj));
    }

    private restoreCampaignPayloadFromStorage(){
        const storedPayload = sessionStorage.getItem(CAMPAIGN_PAYLOAD_STORAGE_KEY);

        if (!storedPayload) return;

        try {
            this._sessionCampaignPayload = JSON.parse(storedPayload) as SessionCampaignPayload;
        } catch {
            sessionStorage.removeItem(CAMPAIGN_PAYLOAD_STORAGE_KEY);
        }
    }
}